/**
 * Balance cache utilities for handling rate limit errors
 * Caches wallet.check_balance responses in localStorage
 * 
 * IMPORTANT USAGE RULES:
 * 1. Only save to cache AFTER successful API responses (response.result exists)
 * 2. DO NOT save to cache if you received a rate limit error (429)
 * 3. Check for rate limit errors with isRateLimitError() before deciding to cache
 * 4. Load from cache as fallback when rate limit errors occur
 * 
 * Example:
 * ```typescript
 * const response = await rpcCall("wallet.check_balance", {...});
 * if (response.result) {
 *     // Success - save to cache
 *     saveBalanceToCache(..., response.result);
 * } else if (isRateLimitError(response.error?.message)) {
 *     // Rate limit - try to load from cache
 *     const cached = loadBalanceFromCache(...);
 *     if (cached) {
 *         // Use cached data
 *     }
 * }
 * ```
 */

export interface CachedBalanceResult {
    can_sell: boolean;
    ton_balance: number;
    jetton_balance: number;
    min_ton_required: number;
    jetton_required: number;
    errors?: {
        insufficient_ton: boolean;
        insufficient_jettons: boolean;
    };
    timestamp: number;
    wallet: string;
    jetton_address: string;
    amount: number;
}

const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const CACHE_PREFIX = 'balance_check_';

/**
 * Generate cache key for balance check
 */
export function getBalanceCacheKey(
    wallet: string,
    jettonAddress: string,
    amount: number,
    network: string
): string {
    // Round amount to 4 decimals to avoid cache misses on tiny differences
    const roundedAmount = Math.round(amount * 10000) / 10000;
    return `${CACHE_PREFIX}${wallet}_${jettonAddress}_${roundedAmount}_${network}`;
}

/**
 * Load balance check result from cache
 */
export function loadBalanceFromCache(
    wallet: string,
    jettonAddress: string,
    amount: number,
    network: string
): Omit<CachedBalanceResult, 'timestamp' | 'wallet' | 'jetton_address' | 'amount'> | null {
    try {
        const cacheKey = getBalanceCacheKey(wallet, jettonAddress, amount, network);
        const cached = localStorage.getItem(cacheKey);
        
        if (cached) {
            const cachedData: CachedBalanceResult = JSON.parse(cached);
            const age = Date.now() - cachedData.timestamp;
            
            if (age < CACHE_TTL) {
                console.log(`[BalanceCache] Loaded from cache (age: ${Math.round(age / 1000)}s) for ${jettonAddress}`);
                return {
                    can_sell: cachedData.can_sell,
                    ton_balance: cachedData.ton_balance,
                    jetton_balance: cachedData.jetton_balance,
                    min_ton_required: cachedData.min_ton_required,
                    jetton_required: cachedData.jetton_required,
                    errors: cachedData.errors
                };
            } else {
                console.log(`[BalanceCache] Cache expired (age: ${Math.round(age / 1000)}s), removing`);
                localStorage.removeItem(cacheKey);
            }
        }
    } catch (err) {
        console.error("[BalanceCache] Failed to load from cache:", err);
    }
    
    return null;
}

/**
 * Save balance check result to cache
 * Note: Only call this after successful API responses (not rate limit errors)
 */
export function saveBalanceToCache(
    wallet: string,
    jettonAddress: string,
    amount: number,
    network: string,
    balanceData: {
        can_sell: boolean;
        ton_balance: number;
        jetton_balance: number;
        min_ton_required: number;
        jetton_required: number;
        errors?: {
            insufficient_ton: boolean;
            insufficient_jettons: boolean;
        };
    }
): void {
    try {
        // Validate data before caching - don't cache suspicious all-zero balances
        // (unless it's a valid case where user actually has 0 balance)
        const hasBalanceData = balanceData.ton_balance > 0 || balanceData.jetton_balance > 0;
        
        if (!hasBalanceData && jettonAddress !== "TON") {
            console.warn(`[BalanceCache] ⚠️ Skipping cache: suspicious zero balances for ${jettonAddress}`);
            return;
        }
        
        const cacheKey = getBalanceCacheKey(wallet, jettonAddress, amount, network);
        const cacheData: CachedBalanceResult = {
            ...balanceData,
            timestamp: Date.now(),
            wallet,
            jetton_address: jettonAddress,
            amount
        };
        
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
        console.log(`[BalanceCache] Saved to cache for ${jettonAddress} (ton: ${balanceData.ton_balance.toFixed(2)}, jetton: ${balanceData.jetton_balance.toFixed(2)})`);
    } catch (err) {
        console.error("[BalanceCache] Failed to save to cache:", err);
    }
}

/**
 * Check if error is a rate limit error
 */
export function isRateLimitError(errorMessage: string | undefined): boolean {
    if (!errorMessage) return false;
    
    const rateLimitKeywords = ['rate limit', '429', 'too many requests', 'timeout'];
    const lowerError = errorMessage.toLowerCase();
    
    return rateLimitKeywords.some(keyword => lowerError.includes(keyword));
}

/**
 * Clear old cache entries (optional cleanup utility)
 */
export function clearOldBalanceCache(): void {
    try {
        const keysToRemove: string[] = [];
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith(CACHE_PREFIX)) {
                try {
                    const cached = localStorage.getItem(key);
                    if (cached) {
                        const cachedData: CachedBalanceResult = JSON.parse(cached);
                        const age = Date.now() - cachedData.timestamp;
                        
                        if (age >= CACHE_TTL) {
                            keysToRemove.push(key);
                        }
                    }
                } catch (err) {
                    // Invalid cache entry, remove it
                    keysToRemove.push(key);
                }
            }
        }
        
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log(`[BalanceCache] Cleared ${keysToRemove.length} old cache entries`);
    } catch (err) {
        console.error("[BalanceCache] Failed to clear old cache:", err);
    }
}
